import { type ReactNode } from 'react';
import { Combobox, type ComboboxOption } from '../Combobox';
import { useFormField } from '../FormField';

/**
 * Select — 네이티브 `<select>` **108회 / 51파일** 이 옮겨 올 자리 (디자인 정본 B4).
 *
 * ★★★ **새 구현이 아니라 «껍데기»다.** 고르는 동작·키보드·읽어 주기는 전부 기존
 *    `Combobox`(WAI-ARIA combobox 패턴을 외부 라이브러리 없이 구현한 것)가 한다.
 *    셋으로 갈라진 것을 **하나로 모으는 것**이 이 판의 일이라, 넷째를 만들면 안 된다.
 *      · Radix(`ui/select.tsx`) 4곳 — 앱에서 Tailwind·`cn()` 을 쓰는 유일한 파일이라 토큰 체계 밖
 *      · 자체 `Combobox` 18곳 ← **정본**
 *      · 네이티브 `<select>` 108회 ← 여기로 온다
 *
 * ★★ **폭은 호출부가 못 박는다.** `Combobox` 는 `width: 100%` 라, flex 줄에 넣고
 *    `flex: none` 만 주면 **줄을 통째로 먹는다** — 과거에 이걸로 화면이 깨졌다.
 *    줄 안에 넣을 땐 `style={{ width: 180 }}` 처럼 폭을 준다.
 *
 * ★ 트리거는 **Input 과 같은 높이·패딩·테두리·상태**를 쓴다(`--field`). 나란히 놓이기 때문이다.
 * ★ FormField 안에 있으면 id · aria-describedby · aria-invalid · required · disabled 를
 *   **자동으로** 물려받는다.
 */
export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectProps {
  value: string | null;
  onChange: (value: string) => void;
  options: ReadonlyArray<ComboboxOption>;
  placeholder?: string;
  /** 목록이 길 때 — 글자로 걸러낼 수 있게 한다. */
  searchable?: boolean;
  size?: SelectSize;
  disabled?: boolean;
  invalid?: boolean;
  /** FormField 밖에서 쓸 때의 이름. */
  ariaLabel?: string;
  className?: string;
  renderOption?: (option: ComboboxOption) => ReactNode;
  /**
   * 목록을 **body 로 띄운다** (2026-09-06). `overflow` 를 가진 상자 안에 설 때 켠다 —
   * 모달 본문(`.wc-modal__body`) · 표 상자(`.wc-table-wrap`) 안에서는 안 켜면 **잘린다.**
   * ★ 기본은 꺼짐이라 안 주면 종전과 똑같다.
   */
  portal?: boolean;
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  searchable = false,
  size = 'md',
  disabled,
  invalid,
  ariaLabel,
  className,
  renderOption,
  portal,
}: SelectProps) {
  const field = useFormField();

  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      searchable={searchable}
      disabled={disabled ?? field?.disabled ?? false}
      invalid={invalid ?? field?.invalid ?? false}
      required={field?.required ?? false}
      id={field?.controlId}
      describedBy={field?.describedBy}
      ariaLabel={ariaLabel}
      renderOption={renderOption}
      portal={portal}
      className={['wc-combobox--field', `wc-combobox--${size}`, className ?? ''].filter(Boolean).join(' ')}
    />
  );
}
